/**
 * Certification marks held by the project.
 *
 * Every entry points at a real logo file in `public/assets/certifications/`.
 * Nothing here is generated or redrawn — a standard with no logo file simply
 * has no entry, rather than being represented by a stand-in graphic. Where a
 * standard has no entry, its card falls back to a plain UI icon instead (see
 * Pages/Services/servicesIcons.js `standardIcons`) — never a stand-in mark.
 *
 * Every standard named on the Services page now has a real mark, so the
 * fallback path below is currently unreached — it stays as the safety net for
 * any standard added later before its logo file arrives.
 */

const CERT_DIR = `${process.env.PUBLIC_URL}/assets/certifications`;

const logo = (file, name, alt) => ({
  name,
  alt,
  // Encoded because one of the supplied filenames contains a space.
  src: `${CERT_DIR}/${encodeURIComponent(file)}`,
});

export const CERTIFICATION_LOGOS = {
  "iso-9001": logo("iso9001.png", "ISO 9001", "ISO 9001 Quality Management System"),
  "iso-14001": logo(
    "iso14001.jfif",
    "ISO 14001",
    "ISO 14001 Environmental Management System"
  ),
  "iso-45001": logo(
    "iso45001.png",
    "ISO 45001",
    "ISO 45001 Occupational Health & Safety"
  ),
  "iso-22000": logo(
    "iso2200.png",
    "ISO 22000",
    "ISO 22000 Food Safety Management System"
  ),
  haccp: logo("haccp.jfif", "HACCP", "HACCP Certification"),
  halal: logo("halal.jpg", "Halal", "Halal Certification"),
  "fssc-22000": logo("FSSC 22000.png", "FSSC 22000", "FSSC 22000"),
  "iso-27001": logo(
    "iso27001.webp",
    "ISO 27001",
    "ISO 27001 Information Security Management System"
  ),
  "iso-50001": logo(
    "iso50001.webp",
    "ISO 50001",
    "ISO 50001 Energy Management System"
  ),
  gmp: logo("gmp.webp", "GMP", "GMP & GHP Implementation"),
  brcgs: logo("brcgs.webp", "BRCGS", "BRCGS Food Safety"),
  ecovadis: logo("ecovadis.webp", "EcoVadis", "EcoVadis Consulting"),
  esg: logo("esg.webp", "ESG", "ESG Consulting"),
  "fsc-coc": logo("fsc-coc.webp", "FSC CoC", "FSC Chain of Custody"),
  gots: logo("gots.webp", "GOTS", "GOTS Certification"),
  gri: logo("gri.webp", "GRI", "GRI Reporting Support"),
  grs: logo("grs.webp", "GRS", "GRS Certification"),
  pefc: logo("pefc.webp", "PEFC", "PEFC Certification"),
  "rcs-ocs": logo("rcs-ocs.webp", "RCS & OCS", "RCS & OCS Certification"),
  sa8000: logo("sa8000.webp", "SA8000", "SA8000 Compliance"),
  "sedex-smeta": logo("sedex-smeta.webp", "Sedex / SMETA", "Sedex / SMETA"),
  wrap: logo("wrap.webp", "WRAP", "WRAP Certification"),

  /* Lead-auditor course marks, added 2026-09-14. THREE OF THE SUPPLIED FILES
     WERE MISNAMED and are registered under what the artwork actually reads,
     not the filename: ISO27302.png shows 27032:2023, ISO27304.png shows
     27034:2011, and "ISO 31001.png" shows 31000:2018. Registering them by
     filename would have put a wrong standard number on three cards. */
  "as-9100": logo("as-9100.webp", "AS 9100", "AS 9100 Aerospace Quality Management System"),
  "ce-mark": logo("ce-mark.webp", "CE Mark", "CE Mark"),
  "eu-gdpr": logo("eu-gdpr.webp", "EU GDPR", "EU General Data Protection Regulation"),
  "iatf-16949": logo("iatf-16949.webp", "IATF 16949", "IATF 16949 Automotive Quality Management System"),
  "iso-10001": logo("iso-10001.webp", "ISO 10001", "ISO 10001 Customer Satisfaction Codes of Conduct"),
  "iso-13485": logo("iso-13485.webp", "ISO 13485", "ISO 13485 Medical Devices Quality Management"),
  "iso-15189": logo("iso-15189.webp", "ISO 15189", "ISO 15189 Medical Laboratories"),
  "iso-17020": logo("iso-17020.webp", "ISO 17020", "ISO 17020 Inspection Bodies"),
  "iso-17021": logo("iso-17021.webp", "ISO 17021", "ISO 17021 Certification Bodies"),
  "iso-17025": logo("iso-17025.webp", "ISO 17025", "ISO 17025 Testing & Calibration Laboratories"),
  "iso-18001": logo("iso-18001.webp", "ISO 18001", "ISO 18001 Occupational Health & Safety"),
  "iso-20000": logo("iso-20000.webp", "ISO 20000", "ISO 20000 IT Service Management"),
  "iso-21001": logo("iso-21001.webp", "ISO 21001", "ISO 21001 Educational Organizations Management"),
  "iso-22301": logo("iso-22301.webp", "ISO 22301", "ISO 22301 Business Continuity Management System"),
  "iso-26000": logo("iso-26000.webp", "ISO 26000", "ISO 26000 Social Responsibility"),
  "iso-27005": logo("iso-27005.webp", "ISO 27005", "ISO 27005 Information Security Risk Management"),
  "iso-27032": logo("iso-27032.webp", "ISO 27032", "ISO 27032 Cybersecurity Guidelines"),
  "iso-27034": logo("iso-27034.webp", "ISO 27034", "ISO 27034 Application Security"),
  "iso-29001": logo("iso-29001.webp", "ISO 29001", "ISO 29001 Petroleum, Petrochemical and Natural Gas"),
  "iso-29990": logo("iso-29990.webp", "ISO 29990", "ISO 29990 Learning Services"),
  "iso-31000": logo("iso-31000.webp", "ISO 31000", "ISO 31000 Risk Management"),
  "iso-37001": logo("iso-37001.webp", "ISO 37001", "ISO 37001 Anti-Bribery Management System"),
  "pci-dss": logo("pci-dss.webp", "PCI DSS", "PCI DSS Payment Card Industry Data Security Standard"),
  "tl-9000": logo("tl-9000.webp", "TL 9000", "TL 9000 Telecommunications Quality Management"),
};

/** Resolve a list of logo keys, dropping any the project has no asset for. */
export const getCertificationLogos = (keys = []) =>
  keys.map((key) => CERTIFICATION_LOGOS[key]).filter(Boolean);

export default CERTIFICATION_LOGOS;
